import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import AddOutlined from "@mui/icons-material/AddOutlined";
import AttachFileOutlined from "@mui/icons-material/AttachFileOutlined";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineOutlined from "@mui/icons-material/DeleteOutlineOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import PsychologyOutlined from "@mui/icons-material/PsychologyOutlined";
import SendOutlined from "@mui/icons-material/SendOutlined";
import PageHeader from "../../common/PageHeader";
import { GlobalContext } from "../../context/GlobalContext";
import { UserContext } from "../../context/UserContext";
import { completeResearchChat } from "./researchChatApi";
import {
  deleteResearchAttachment,
  downloadResearchAttachment,
  saveResearchAttachment,
} from "./researchChatFiles";
import {
  createResearchSession,
  deriveSessionTitle,
  loadResearchSessions,
  saveResearchSessions,
} from "./researchChatStorage";

const DEFAULT_SYSTEM_PROMPT =
  "You are a thorough research assistant. Provide clear, well-structured answers with plain headings and bullet points when helpful. " +
  "Do not use Markdown syntax (no #, **, backticks, or pipe tables). Use plain text only. " +
  "Be factual, note uncertainties, and suggest practical follow-up questions or next research steps. " +
  "Use the full conversation history to stay consistent on follow-up messages.";

const ACCEPTED_FILE_TYPES =
  ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/** Convert Markdown-ish LLM output into clean readable plain text. */
export function stripMarkdown(text) {
  let s = String(text || "");

  // Unescape common JSON / LLM escape sequences first
  if (s.includes("\\n") || s.includes('\\"') || s.includes("\\t")) {
    s = s
      .replace(/\\r\\n/g, "\n")
      .replace(/\\n/g, "\n")
      .replace(/\\t/g, "\t")
      .replace(/\\"/g, '"')
      .replace(/\\'/g, "'");
  }

  // Fenced code blocks → inner text
  s = s.replace(/```[\w+-]*\n?([\s\S]*?)```/g, (_, code) => String(code || "").trim());
  // Inline code
  s = s.replace(/`([^`]+)`/g, "$1");
  // Images / links
  s = s.replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1");
  s = s.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
  // Headings
  s = s.replace(/^#{1,6}\s+/gm, "");
  // Bold / italic
  s = s.replace(/\*\*\*(.+?)\*\*\*/gs, "$1");
  s = s.replace(/___(.+?)___/gs, "$1");
  s = s.replace(/\*\*(.+?)\*\*/gs, "$1");
  s = s.replace(/__(.+?)__/gs, "$1");
  s = s.replace(/(?<!\w)\*(?!\s)(.+?)(?<!\s)\*(?!\w)/gs, "$1");
  s = s.replace(/(?<!\w)_(?!\s)(.+?)(?<!\s)_(?!\w)/gs, "$1");
  // Strikethrough
  s = s.replace(/~~(.+?)~~/gs, "$1");
  // Horizontal rules
  s = s.replace(/^\s*([-*_]\s*){3,}\s*$/gm, "");
  // Blockquotes
  s = s.replace(/^>\s?/gm, "");
  // Unordered lists
  s = s.replace(/^\s*[-*+]\s+/gm, "• ");
  // Markdown tables → plain lines
  s = s.replace(/(?:^\s*\|.*\|\s*(?:\n|$))+/gm, (block) => {
    const lines = block.split("\n").filter((line) => line.trim());
    const rows = lines
      .filter((line) => !/^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(line))
      .map((line) =>
        line
          .replace(/^\s*\|/, "")
          .replace(/\|\s*$/, "")
          .split("|")
          .map((c) => c.trim())
          .filter(Boolean)
          .join("  ·  "),
      )
      .filter(Boolean);
    return rows.length ? `${rows.join("\n")}\n` : "";
  });
  // Any leftover pipes / hash markers
  s = s.replace(/\|/g, " ");
  s = s.replace(/(^|\s)#{1,6}(?=\s|$)/g, "$1");
  // Collapse whitespace
  s = s.replace(/[ \t]+\n/g, "\n");
  s = s.replace(/\n{3,}/g, "\n\n");
  return s.trim();
}

function AttachmentCard({ file, onDelete }) {
  const theme = useTheme();
  const [busy, setBusy] = React.useState(false);
  const canDownload = Boolean(file?.id);

  const handleDownload = async (e) => {
    e?.stopPropagation?.();
    if (!canDownload || busy) return;
    setBusy(true);
    try {
      await downloadResearchAttachment(file.id);
    } catch (err) {
      window.alert(err?.message || "Could not download this attachment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        maxWidth: 360,
        px: 1,
        py: 0.5,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: alpha(theme.palette.text.primary, 0.03),
      }}
    >
      <DescriptionOutlined sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }} />
      <Typography
        variant="caption"
        noWrap
        title={file.name}
        sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}
      >
        {file.name}
      </Typography>
      <Tooltip title={canDownload ? "Download" : "Download unavailable for this older attachment"}>
        <span>
          <IconButton
            size="small"
            aria-label="Download attachment"
            onClick={handleDownload}
            disabled={!canDownload || busy}
          >
            {busy ? <CircularProgress size={14} /> : <DownloadOutlined sx={{ fontSize: 16 }} />}
          </IconButton>
        </span>
      </Tooltip>
      {onDelete ? (
        <Tooltip title="Remove attachment">
          <IconButton
            size="small"
            aria-label="Delete attachment"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(file);
            }}
            disabled={busy}
          >
            <DeleteOutlineOutlined sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Stack>
  );
}

function MessageBubble({ message, onDeleteAttachment }) {
  const theme = useTheme();
  const isUser = message.role === "user";
  const [copied, setCopied] = React.useState(false);
  const displayText = isUser ? String(message.content || "") : stripMarkdown(message.content);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore clipboard errors */
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        mb: 1.5,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: "min(760px, 92%)",
          px: 2,
          py: 1.5,
          borderRadius: 2,
          bgcolor: isUser
            ? alpha(theme.palette.primary.main, 0.12)
            : alpha(theme.palette.text.primary, 0.04),
          border: "1px solid",
          borderColor: isUser
            ? alpha(theme.palette.primary.main, 0.25)
            : "divider",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          position: "relative",
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {isUser ? "You" : "Research"}
          </Typography>
          {!isUser && displayText ? (
            <Tooltip title={copied ? "Copied" : "Copy response"}>
              <IconButton size="small" onClick={handleCopy} aria-label="Copy response">
                {copied ? (
                  <CheckOutlined sx={{ fontSize: 16 }} color="success" />
                ) : (
                  <ContentCopyOutlined sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
        {(message.attachments || []).length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center" sx={{ mb: 1 }}>
            {message.attachments.map((file) => (
              <AttachmentCard
                key={file.id || file.name}
                file={file}
                onDelete={
                  onDeleteAttachment
                    ? () => onDeleteAttachment(message.id, file)
                    : undefined
                }
              />
            ))}
          </Stack>
        )}
        {displayText ? (
          <Typography variant="body2" component="div" sx={{ lineHeight: 1.65 }}>
            {displayText}
          </Typography>
        ) : null}
      </Paper>
    </Box>
  );
}

export default function ResearchChat({ lang }) {
  const theme = useTheme();
  const { hitAxios } = React.useContext(GlobalContext);
  const { getUserData } = React.useContext(UserContext);
  const [sessions, setSessions] = React.useState(() => loadResearchSessions());
  const [activeId, setActiveId] = React.useState(() => loadResearchSessions()[0]?.id || null);
  const [draft, setDraft] = React.useState("");
  const [systemPrompt, setSystemPrompt] = React.useState(DEFAULT_SYSTEM_PROMPT);
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [pendingFile, setPendingFile] = React.useState(null);
  const fileInputRef = React.useRef(null);
  const scrollRef = React.useRef(null);

  const activeSession = React.useMemo(
    () => sessions.find((s) => s.id === activeId) || null,
    [sessions, activeId],
  );

  const messages = activeSession?.messages || [];
  const sessionDocuments = activeSession?.documents || [];

  React.useEffect(() => {
    if (!sessions.length) {
      const fresh = createResearchSession({ systemPrompt: DEFAULT_SYSTEM_PROMPT });
      setSessions([fresh]);
      setActiveId(fresh.id);
      saveResearchSessions([fresh]);
      return;
    }
    if (!activeId || !sessions.some((s) => s.id === activeId)) {
      setActiveId(sessions[0].id);
    }
  }, [sessions, activeId]);

  React.useEffect(() => {
    if (activeSession?.systemPrompt) {
      setSystemPrompt(activeSession.systemPrompt);
    }
  }, [activeSession?.id, activeSession?.systemPrompt]);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, sending, activeId]);

  const persistSessions = React.useCallback((updater) => {
    setSessions((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      saveResearchSessions(next);
      return next;
    });
  }, []);

  const updateActiveSession = React.useCallback(
    (patch) => {
      if (!activeId) return;
      const now = Date.now();
      persistSessions((prev) =>
        prev.map((s) =>
          s.id === activeId
            ? {
                ...s,
                ...patch,
                updatedAt: now,
              }
            : s,
        ),
      );
    },
    [activeId, persistSessions],
  );

  const handleNewChat = () => {
    const fresh = createResearchSession({ systemPrompt });
    persistSessions((prev) => [fresh, ...prev]);
    setActiveId(fresh.id);
    setDraft("");
    setPendingFile(null);
    setError(null);
  };

  const handleDeleteSession = (id) => {
    const doomed = sessions.find((s) => s.id === id);
    const attachmentIds = (doomed?.messages || [])
      .flatMap((m) => m.attachments || [])
      .map((a) => a.id)
      .filter(Boolean);
    attachmentIds.forEach((attId) => {
      deleteResearchAttachment(attId).catch(() => {});
    });
    persistSessions((prev) => {
      const next = prev.filter((s) => s.id !== id);
      if (!next.length) {
        const fresh = createResearchSession({ systemPrompt });
        setActiveId(fresh.id);
        return [fresh];
      }
      if (activeId === id) setActiveId(next[0].id);
      return next;
    });
  };

  const handlePickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".pdf") && !lower.endsWith(".doc") && !lower.endsWith(".docx")) {
      setError("Only PDF, DOC, and DOCX files are supported.");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setError("File is too large. Maximum size is 15 MB.");
      return;
    }
    setError(null);
    setPendingFile(file);
  };

  const handleDeleteAttachment = async (messageId, file) => {
    if (!messageId || !file) return;
    if (file.id) {
      try {
        await deleteResearchAttachment(file.id);
      } catch {
        /* best-effort */
      }
    }
    persistSessions((prev) =>
      prev.map((s) => {
        if (s.id !== activeId) return s;
        const nextMessages = (s.messages || []).map((m) => {
          if (m.id !== messageId) return m;
          const attachments = (m.attachments || []).filter(
            (a) => (file.id && a.id !== file.id) || (!file.id && a.name !== file.name),
          );
          return { ...m, attachments };
        });
        const nextDocuments = (s.documents || []).filter((d) => d.name !== file.name);
        return {
          ...s,
          messages: nextMessages,
          documents: nextDocuments,
          updatedAt: Date.now(),
        };
      }),
    );
  };

  const handleSend = async () => {
    const text = draft.trim();
    if ((!text && !pendingFile) || sending || !activeSession) return;

    setError(null);
    setSending(true);
    const fileToSend = pendingFile;
    setDraft("");
    setPendingFile(null);

    const attachmentId = fileToSend
      ? `att_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      : null;

    if (fileToSend && attachmentId) {
      try {
        await saveResearchAttachment(attachmentId, fileToSend);
      } catch (err) {
        setError(err?.message || "Could not store attachment for download.");
        setDraft(text);
        setPendingFile(fileToSend);
        setSending(false);
        return;
      }
    }

    const userMessage = {
      id: `m_${Date.now()}_u`,
      role: "user",
      content: text || `Uploaded document: ${fileToSend.name}`,
      attachments: fileToSend
        ? [
            {
              id: attachmentId,
              name: fileToSend.name,
              mime: fileToSend.type || "",
              size: fileToSend.size || 0,
            },
          ]
        : undefined,
      createdAt: Date.now(),
    };

    const priorMessages = messages;
    const priorDocuments = sessionDocuments;
    const nextMessages = [...priorMessages, userMessage];

    persistSessions((prev) =>
      prev.map((s) =>
        s.id === activeId
          ? {
              ...s,
              messages: nextMessages,
              title: deriveSessionTitle(nextMessages),
              systemPrompt,
              updatedAt: Date.now(),
            }
          : s,
      ),
    );

    try {
      const data = await completeResearchChat(hitAxios, {
        messages: nextMessages.map((m) => ({
          role: m.role,
          content: m.content,
          attachments: m.attachments,
        })),
        systemPrompt,
        documents: priorDocuments,
        file: fileToSend || undefined,
      });

      const nextDocuments = [...priorDocuments];
      if (data.parsed_document) {
        const exists = nextDocuments.some((d) => d.name === data.parsed_document.name);
        if (!exists) {
          nextDocuments.push({
            name: data.parsed_document.name,
            text: data.parsed_document.text,
            char_count: data.parsed_document.char_count,
            truncated: data.parsed_document.truncated,
          });
        }
      }

      const assistantMessage = {
        id: `m_${Date.now()}_a`,
        role: "assistant",
        content: data.reply,
        createdAt: Date.now(),
      };

      const withReply = [...nextMessages, assistantMessage];
      persistSessions((prev) =>
        prev.map((s) =>
          s.id === activeId
            ? {
                ...s,
                messages: withReply,
                documents: nextDocuments,
                title: deriveSessionTitle(withReply),
                systemPrompt,
                updatedAt: Date.now(),
              }
            : s,
        ),
      );
      try {
        await getUserData?.();
      } catch {
        /* credit balance refresh is best-effort */
      }
    } catch (err) {
      setError(err?.message || "Could not complete research.");
      setDraft(text);
      setPendingFile(fileToSend);
      persistSessions((prev) =>
        prev.map((s) =>
          s.id === activeId
            ? {
                ...s,
                messages: priorMessages,
                documents: priorDocuments,
                title: deriveSessionTitle(priorMessages),
                systemPrompt,
                updatedAt: Date.now(),
              }
            : s,
        ),
      );
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: { xs: "auto", md: "calc(100vh - 120px)" },
        minHeight: { xs: "70vh", md: 0 },
      }}
    >
      <PageHeader
        title={lang?.researchChatNav || "Research Chat"}
        subtitle={
          lang?.researchChatSubtitle ||
          "Prompt ChatGPT for research, follow up with questions, and keep conversation history."
        }
        icon={PsychologyOutlined}
        primaryAction={
          <Button variant="contained" startIcon={<AddOutlined />} onClick={handleNewChat}>
            {lang?.newChat || "New chat"}
          </Button>
        }
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "260px 1fr" },
          gap: 2,
          flex: 1,
          minHeight: 0,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            minHeight: 0,
            height: "100%",
          }}
        >
          <Box sx={{ px: 2, py: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {lang?.history || "History"}
            </Typography>
          </Box>
          <Divider />
          <List dense sx={{ flex: 1, overflow: "auto", py: 0, minHeight: 0 }}>
            {sessions.map((session) => (
              <ListItemButton
                key={session.id}
                selected={session.id === activeId}
                onClick={() => {
                  setActiveId(session.id);
                  setError(null);
                }}
                sx={{ alignItems: "flex-start" }}
              >
                <ListItemText
                  primary={session.title || "New research"}
                  secondary={new Date(session.updatedAt || session.createdAt).toLocaleString()}
                  primaryTypographyProps={{ noWrap: true, fontWeight: 600, fontSize: 13 }}
                  secondaryTypographyProps={{ noWrap: true, fontSize: 11 }}
                />
                <IconButton
                  size="small"
                  edge="end"
                  aria-label="delete session"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSession(session.id);
                  }}
                >
                  <DeleteOutlineOutlined fontSize="small" />
                </IconButton>
              </ListItemButton>
            ))}
          </List>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            minHeight: { xs: 520, md: 0 },
            height: { md: "100%" },
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider", flexShrink: 0 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {activeSession?.title || "New research"}
            </Typography>
          </Box>

          <Box
            ref={scrollRef}
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              px: { xs: 1.5, sm: 2 },
              py: 2,
              bgcolor: alpha(theme.palette.background.default, 0.5),
            }}
          >
            {!messages.length && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Ask a research question or attach a PDF/DOC/DOCX file. Follow-up messages use the
                full thread and uploaded documents as context.
              </Alert>
            )}

            {sessionDocuments.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={0.75}>
                  Session documents
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  {sessionDocuments.map((doc) => (
                    <Chip
                      key={doc.name}
                      size="small"
                      icon={<DescriptionOutlined />}
                      label={doc.name}
                      color="primary"
                      variant="outlined"
                      onDelete={() => {
                        const matchingIds = messages
                          .flatMap((m) => m.attachments || [])
                          .filter((a) => a.name === doc.name && a.id)
                          .map((a) => a.id);
                        matchingIds.forEach((attId) => {
                          deleteResearchAttachment(attId).catch(() => {});
                        });
                        persistSessions((prev) =>
                          prev.map((s) => {
                            if (s.id !== activeId) return s;
                            return {
                              ...s,
                              documents: (s.documents || []).filter((d) => d.name !== doc.name),
                              messages: (s.messages || []).map((m) => ({
                                ...m,
                                attachments: (m.attachments || []).filter((a) => a.name !== doc.name),
                              })),
                              updatedAt: Date.now(),
                            };
                          }),
                        );
                      }}
                      deleteIcon={<DeleteOutlineOutlined />}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {messages.map((m) => (
              <MessageBubble
                key={m.id}
                message={m}
                onDeleteAttachment={m.role === "user" ? handleDeleteAttachment : undefined}
              />
            ))}

            {sending && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Researching…
                </Typography>
              </Stack>
            )}
          </Box>

          <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider", flexShrink: 0 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              size="small"
              label={lang?.systemPrompt || "System prompt (optional)"}
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              sx={{ mb: 1.5 }}
              onBlur={() => updateActiveSession({ systemPrompt })}
            />

            <Stack direction="row" spacing={1} alignItems="flex-end">
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept={ACCEPTED_FILE_TYPES}
                onChange={handlePickFile}
              />
              <IconButton
                aria-label="attach document"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending}
                sx={{ mb: 0.5 }}
              >
                <AttachFileOutlined />
              </IconButton>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                placeholder={
                  lang?.researchPromptPlaceholder ||
                  "Ask a research question, attach PDF/DOC, or both…"
                }
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sending}
              />
              <Button
                variant="contained"
                endIcon={<SendOutlined />}
                onClick={handleSend}
                disabled={sending || (!draft.trim() && !pendingFile)}
                sx={{ minWidth: 108, height: 40 }}
              >
                Send
              </Button>
            </Stack>

            {pendingFile && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <Chip
                  icon={<DescriptionOutlined />}
                  label={pendingFile.name}
                  onDelete={() => setPendingFile(null)}
                  deleteIcon={<CloseOutlined />}
                  variant="outlined"
                />
              </Stack>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
